import type { ReactNode} from 'react';
import { useEffect } from 'react';

import classNames from 'classnames';

import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import { StoryCard } from '@store:web/components/pages/StoryPage/StoryCard';
import { cn } from '@ui/uikit/utils';

import type { JoinCampaignStepType } from './getStepsToJoinCampaign';
import { JoinCampaignButton } from './JoinCampaignButton';
import { StepActionButton } from './StepActionButton/StepActionButton';

export interface StepsToJoinCampaignStep {
  id: string;
  title: ReactNode;
  type?: JoinCampaignStepType;
  content?: ReactNode;
  className?: string;
  href?: string;
  buttonLabel?: string;
}

interface StepsToJoinCampaignProps {
  steps: StepsToJoinCampaignStep[];
  campaignUrl?: string;
  className?: string;
  headerTitle?: ReactNode;
  hideListNumbers?: boolean;
}

const StepsToJoinCampaign = ({
  campaignUrl,
  steps,
  className,
  headerTitle,
  hideListNumbers = false,
}: StepsToJoinCampaignProps) => {
  const showJoinCampaignButton = !!campaignUrl;
  const { resetValue: resetSelfDataCheckLocalStorage } = useSelfDataCheckLocalStorage();

  useEffect(() => {
    // NOTE: reset local storage to initial state when page is opened
    // to make it consistent across tabs
    resetSelfDataCheckLocalStorage();
  }, [resetSelfDataCheckLocalStorage]);

  return (
    <div className={classNames('flex flex-col', className)}>
      <StoryCard title={headerTitle}>
        <ul className="flex flex-col gap-4 md:gap-6">
          {steps.map((step, i) => {
            const showActionButton = !!step.type;

            return (
              <li
                key={step.id}
                className={cn(
                  'flex max-md:flex-col p-4 md:p-6 gap-5 md:gap-3 border border-primary-200 rounded-lg items-center',
                  step.className,
                )}
              >
                <div className="flex gap-3 flex-1">
                  {!hideListNumbers && (
                    <span className="rounded-full self-start flex-shrink-0 flex justify-center text-lg md:text-2xl font-semibold items-center w-7 md:w-8 bg-gradient-to-r text-neutral-50 from-[#FF6647] via-[#E14BFF] to-[#4AA5FF]">
                      {i + 1}
                    </span>
                  )}

                  <div className="flex flex-col gap-2.5 w-full">
                    <p className="text-lg md:text-2xl font-semibold">{step.title}</p>

                    {step.content}
                  </div>
                </div>

                {showActionButton && (
                  <div className="max-md:self-end">
                    <StepActionButton campaignUrl={campaignUrl} step={step} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <p className="max-md:text-xs text-right text-neutral-500 mt-2.5">
          Only one account per user is permitted. Duplicate accounts will be blocked from the
          campaign.
        </p>
      </StoryCard>

      {showJoinCampaignButton && (
        <JoinCampaignButton campaignUrl={campaignUrl} className="mx-auto" />
      )}
    </div>
  );
};

export default StepsToJoinCampaign;
